import { motion } from "framer-motion";
import { Droplets, Zap, KeyRound, MessageCircle, CheckCircle2 } from "lucide-react";
import { waLink } from "@/lib/site";

const SERVICES = [
  {
    id: "plumbing",
    icon: Droplets,
    title: "Idraulica Rapida",
    desc: "Perdite dai sifoni, galleggianti WC, flessibili del boiler, intasamenti urgenti. Il ricambio giusto è già nello zaino.",
    tags: ["Perdite & sifoni", "WC & galleggianti", "Boiler e flessibili"],
    span: "md:col-span-7",
    img: "https://images.unsplash.com/photo-1581166397057-235af2b3c6dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    id: "electrical",
    icon: Zap,
    title: "Piccola Elettricità",
    desc: "Salvavita che scatta, prese bruciate, magnetotermici, lampadari e piastre a induzione del tuo B&B.",
    tags: ["Cortocircuiti", "Prese & interruttori", "Lampadari"],
    span: "md:col-span-5",
    img: "https://images.unsplash.com/photo-1553873002-785d775854c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    id: "locks-shutters",
    icon: KeyRound,
    title: "Serrature & Tapparelle",
    desc: "Porte bloccate, cilindri europei anti-bumping, cinghie di tapparelle spezzate e maniglie sbloccate.",
    tags: ["Porte bloccate", "Cilindri europei", "Cinghie tapparelle"],
    span: "md:col-span-12",
    img: "https://images.unsplash.com/photo-1717332152812-8c1ad08833c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export default function Services() {
  return (
    <section id="servizi" className="bg-[#060B19] py-16 sm:py-28" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="font-mono-accent text-xs font-bold uppercase tracking-[0.22em] text-[#FFB800]">
            01 — Cosa ripariamo
          </p>
          <h2 className="font-display mt-3 text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            Piccoli guasti, <span className="text-[#FF5500]">grande velocità.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            Niente cantieri infiniti: solo micro-manutenzioni risolte in giornata, con preventivo confermato via video prima di partire.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-12">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0D1833] transition-all duration-300 hover:-translate-y-1 hover:border-[#FF5500]/45 ${s.span} ${
                s.span === "md:col-span-12" ? "md:flex" : ""
              }`}
            >
              <div className={s.span === "md:col-span-12" ? "p-6 sm:p-8 md:w-1/2" : "p-6 sm:p-8"}>
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#FFB800]/15">
                  <s.icon className="h-6 w-6 text-[#FFB800]" strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{s.desc}</p>
                <ul className="mt-4 space-y-2">
                  {s.tags.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-4 w-4 text-[#25D366]" />
                      {t}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(`Ciao El Mastro! Ho bisogno di un intervento di ${s.title}. Vi invio una foto/video del guasto.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${s.id}`}
                  className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-[#FF5500]/50 px-5 text-sm font-bold text-[#FF5500] transition-all duration-200 hover:bg-[#FF5500] hover:text-white"
                >
                  <MessageCircle className="h-4 w-4" />
                  Richiedi preventivo foto
                </a>
              </div>
              <div className={s.span === "md:col-span-12" ? "relative min-h-[220px] md:w-1/2" : "relative h-44"}>
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-500 group-hover:scale-105 group-hover:opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1833] via-transparent to-transparent" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
